import type { HierarchyImportRelationshipType } from '../../types';
import type { Diagram, DiagramNode } from '@aha-app/diagrams';
import { generateDiagram } from '@aha-app/diagrams';
import { RECORD_DEFAULT_HEIGHT, RECORD_DEFAULT_WIDTH } from '../../constants';
import HierarchyResultTransformer, {
  type ChartOrientation,
} from './HierarchyResultTransformer';
import { ImportConfig, HierarchyImportError } from './HierarchyImportTypes';
import { type HierarchyData } from './HierarchyData';

// Helper function extracted from original file
function getOrientation(
  relationshipType: HierarchyImportRelationshipType
): ChartOrientation {
  switch (relationshipType) {
    case 'parents':
      return 'north'; // parents above
    case 'children':
      return 'south'; // children below
    case 'dependencies':
    case 'related':
    default:
      return 'east'; // dependencies to the right
  }
}

// Wrapper for diagram with optimized node lookup
export class DiagramLayout {
  private readonly nodeMap: Map<string, DiagramNode>;

  constructor(public readonly diagram: Diagram) {
    this.nodeMap = new Map();
    for (const node of diagram.database.nodes()) {
      if (node.title) {
        this.nodeMap.set(node.title, node);
      }
    }
  }

  findNodeByTitle(title: string): DiagramNode | undefined {
    return this.nodeMap.get(title);
  }
}

export class DiagramLayoutService {
  async generateLayout(
    hierarchyData: HierarchyData,
    relationshipType: HierarchyImportRelationshipType
  ): Promise<DiagramLayout> {
    try {
      const orientation = getOrientation(relationshipType);
      const transformer = new HierarchyResultTransformer(
        hierarchyData.original,
        orientation
      );

      const diagramText = transformer.toDiagramText();
      const diagram = generateDiagram(diagramText, ImportConfig.DIAGRAM_HINTS);

      // Use proper typing instead of 'any'
      const measureTextFunc = async () => {
        return {
          width: RECORD_DEFAULT_WIDTH,
          height: RECORD_DEFAULT_HEIGHT,
          fontSize: ImportConfig.DEFAULT_FONT_SIZE,
        };
      };

      await diagram.fullLayout({ measureTextFunc });
      return new DiagramLayout(diagram);
    } catch (error) {
      throw HierarchyImportError.layout(
        `Failed to generate diagram layout: ${(error as Error).message}`
      );
    }
  }
}
